import * as React from 'react';
import styled from 'styled-components';
import { useSharedElement } from '@/components/use-shared-element';
import { SharedElementOverlay } from '@/components/shared-element-overlay';
import { CloseButton } from '@/components/close-button';
import { BackdropSpinner } from '@/components/backdrop-spinner';
import {
  GradientLayout,
  GradientLayoutProps,
} from '@/components/gradient-layout';

export interface GradientModalProps extends GradientLayoutProps {
  loading?: boolean;
  showBackdrop: boolean;
  targetPosAndSize: DOMRect;
  fromStyle?: Record<string, any>;
  toStyle?: Record<string, any>;
  fromRef: React.MutableRefObject<HTMLElement>;
  scrollRef?: React.MutableRefObject<HTMLElement>;

  allowMouseMovePropagation?: boolean;

  isVisible?: boolean;
  hideCloseButton?: boolean;
  disableCloseOnBackdropClick?: boolean;

  closeTop?: string;
  closeRight?: string;
  closeStyle?: closeStyle;

  onClose: (() => void) | ((_: any) => Promise<void>);
  noScroll?: boolean;

  aspectRatio?: number;
  headerTop?: number;
  headerHeight?: number;

  isFullScreenTransparent?: boolean;

  backdropBlur?: number;
  overlayBoxShadow?: string;
}

interface closeStyle {
  boxShadow?: string;
  background?: string;
  borderRadius?: string;
  padding?: number;
  opacity?: number;
}

export function GradientModal(props: GradientModalProps) {
  const {
    loading,
    targetPosAndSize,
    showBackdrop,
    fromStyle,
    toStyle,
    fromRef,
    isVisible,
    hideCloseButton,
    header,
    children,
    footer,
    closeTop,
    closeRight,
    onClose,
    disableCloseOnBackdropClick,
    allowMouseMovePropagation,
    aspectRatio,
    headerTop,
    headerHeight,
    isFullScreenTransparent,
    closeStyle,
    backdropBlur,
    overlayBoxShadow,
  } = props;

  const { props: modalProps } = useSharedElement(
    {
      showBackdrop,
      extraFrom: fromStyle,
      extraTo: toStyle,
      aspectRatio,
    },
    fromRef,
    () => targetPosAndSize,
  );

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {isVisible && (
        <SharedElementOverlay
          {...modalProps}
          allowMouseMovePropagation={allowMouseMovePropagation}
          onBackdropClick={(ev) => {
            if (!disableCloseOnBackdropClick && !loading) {
              onClose(ev);
            }
          }}
          isVisible={isVisible}
          isFullScreenTransparent={isFullScreenTransparent}
          backdropBlur={backdropBlur}
          overlayBoxShadow={overlayBoxShadow}
          overlayBorderRadius={toStyle?.borderRadius}
        >
          <ModalContent aria-busy={loading}>
            <GradientLayout
              {...props}
              headerTop={headerTop}
              headerHeight={headerHeight}
              header={header}
              footer={footer}
              preHeader={
                !hideCloseButton &&
                !loading && (
                  <CloseWrapper
                    top={closeTop}
                    right={closeRight}
                    closeStyle={closeStyle}
                    onClick={onClose}
                  >
                    <CloseButton color="#666F7C" />
                  </CloseWrapper>
                )
              }
            >
              {children}
            </GradientLayout>
            <BackdropSpinner
              loading={loading}
              backgroundColor="rgba(255, 255, 255, 0.75)"
              blurAmount={2}
              borderRadius="inherit"
              spinnerSize={32}
            />
          </ModalContent>
        </SharedElementOverlay>
      )}
    </>
  );
}

interface CW {
  top?: string;
  right?: string;
  closeStyle?: closeStyle;
}

const ModalContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: inherit;
`;

const CloseWrapper = styled.div`
  position: absolute;
  top: ${(p: CW) => p?.top || `1.5rem`};
  right: ${(p: CW) => p?.right || `1.5rem`};
  z-index: 100;
  opacity: ${(p: CW) => p?.closeStyle?.opacity ?? 0.37};
  background: ${(p: CW) => p?.closeStyle?.background || `unset`};
  border-radius: ${(p: CW) => p?.closeStyle?.borderRadius || `unset`};
  padding: ${(p: CW) => `${p?.closeStyle?.padding || 0}px`};
  box-shadow: ${(p: CW) => p?.closeStyle?.boxShadow || `unset`};
}

  && > * {
    height: 3rem;
    width: 3rem;
  }
`;
